/* eslint-disable linebreak-style */
import BaseComponent from "sap/ui/core/UIComponent";
import { createDeviceModel } from "./model/models";
import JSONModel from "sap/ui/model/json/JSONModel";
import Device from "sap/ui/Device";
import * as msal from "@azure/msal-browser";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import ApiService from "./src/api/ApiService";
import SentryService from "./src/services/SentryService";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// declare const msal: any;
/**
 * @namespace de.realcore.realseats
 */
export default class Component extends BaseComponent {
    public static readonly metadata = {
        manifest: "json"
    };
    private oDateTimeModel: JSONModel;
    private msalInstance: msal.PublicClientApplication;
    private apiService: ApiService;
    private msalInitialized: Promise<void>;

    public init(): void {
        try {
            // Initialize Sentry context for the application
            this.initializeSentryContext();

            // Set default language to English
            sap.ui.getCore().getConfiguration().setLanguage("en");

            super.init();

            // Complete MSAL initialization sequence BEFORE router initialization
            // This prevents race conditions where API calls happen before authentication is ready
            this.msalInitialized = this._initMSAL()
                .then(async (instance) => {
                    this.msalInstance = instance;

                    // MSAL best practices is calling handleRedirectPromise once during init
                    // and MUST be awaited before any other MSAL operations
                    const redirectResponse = await this.msalInstance.handleRedirectPromise();

                    // If we got a redirect response, we're returning from login
                    if (redirectResponse) {
                        SentryService.addBreadcrumb(
                            "Redirect response received after login",
                            "auth",
                            "info",
                            { accountId: redirectResponse.account?.localAccountId }
                        );
                    }

                    // Check if user is authenticated
                    const accounts = this.msalInstance.getAllAccounts();

                    if (accounts.length === 0) {
                        // No accounts found, need to login
                        // Don't initialize router yet, will redirect to login
                        SentryService.addBreadcrumb(
                            "No accounts found, redirecting to login",
                            "auth",
                            "info"
                        );
                        await this.msalInstance.loginRedirect({
                            scopes: ["api://f6f24b76-b764-45b1-9a56-fe0c18ab1f3b/user"]
                        });
                        // loginRedirect will redirect away, no need to continue initialization
                        return;
                    }

                    // User is authenticated, get token and continue initialization
                    // Call internal token acquisition without ensureMsalInitialized check
                    // since we're already inside the initialization chain
                    await this._acquireToken();

                    // Initialize API Service after MSAL is fully ready
                    this.apiService = new ApiService(this);

                    // Initialize router only after authentication is ready
                    this.getRouter().initialize();
                })
                .catch((error) => {
                    SentryService.captureError(error as Error, {
                        component: "Component",
                        operation: "initMSAL",
                        phase: "initialization"
                    });
                });
            this.setModel(createDeviceModel(), "device");

            const oDeviceModel = new JSONModel({
                isMobile: Device.system.phone || Device.system.tablet
            });
            this.setModel(oDeviceModel, "oRealSeats");

            this.oDateTimeModel = new JSONModel({
                currentDateTime: this.getCurrentDateTime()
            });
            this.setModel(this.oDateTimeModel, "dateTime");

            setInterval(() => {
                this.oDateTimeModel.setProperty("/currentDateTime", this.getCurrentDateTime());
            }, 1000);

            // Set application initialized context
            SentryService.setContext("application", {
                initialized: true,
                timestamp: new Date().toISOString(),
                device: {
                    isMobile: Device.system.phone || Device.system.tablet,
                    isPhone: Device.system.phone,
                    isTablet: Device.system.tablet,
                    isDesktop: Device.system.desktop
                }
            });

            SentryService.addBreadcrumb(
                "Application initialized successfully",
                "lifecycle",
                "info",
                { component: "Component" }
            );
        } catch (error) {
            SentryService.captureError(error as Error, {
                component: "Component",
                operation: "init",
                phase: "initialization"
            });
            throw error;
        }
    }

    /**
     * Initialize Sentry context with application and device information
     */
    private initializeSentryContext(): void {
        try {
            // Set application context
            SentryService.setContext("application", {
                name: "RealSeats",
                version: "1.0.0",
                component: "SAPUI5 Frontend",
                initialized: false
            });

            // Set device context
            SentryService.setContext("device", {
                isMobile: Device.system.phone || Device.system.tablet,
                isPhone: Device.system.phone,
                isTablet: Device.system.tablet,
                isDesktop: Device.system.desktop,
                browser: {
                    name: Device.browser.name,
                    version: Device.browser.version
                },
                os: {
                    name: Device.os.name,
                    version: Device.os.version
                }
            });

            // Set initial tags
            let deviceType = "desktop";
            if (Device.system.phone) {
                deviceType = "phone";
            } else if (Device.system.tablet) {
                deviceType = "tablet";
            }

            SentryService.setTags({
                "app.component": "frontend",
                "app.framework": "sapui5",
                "device.type": deviceType
            });
        } catch (error) {
            console.error("Failed to initialize Sentry context:", error);
        }
    }

    /**
     * Get the API Service instance
     * Provides centralized access to all API operations
     * @returns The API Service instance
     */
    public getApiService(): ApiService {
        if (!this.apiService) {
            throw new Error(
                "API Service is not yet initialized. Ensure MSAL authentication completes before making API calls."
            );
        }
        return this.apiService;
    }

    /**
     * Ensure MSAL is fully initialized before performing authentication operations
     * This method should be called before any operation that requires authentication
     * @returns Promise that resolves when MSAL initialization is complete
     */
    public async ensureMsalInitialized(): Promise<void> {
        if (this.msalInitialized !== undefined) {
            await this.msalInitialized;
        }
    }

    private async _initMSAL(): Promise<msal.PublicClientApplication> {
        const MSALCONFIG = {
            auth: {
                clientId: "f6f24b76-b764-45b1-9a56-fe0c18ab1f3b",
                // eslint-disable-next-line fiori-custom/sap-no-hardcoded-url
                authority: "https://login.microsoftonline.com/99c7da52-56fc-49ca-aa95-8f7fb09c995e"
            },
            cache: {
                cacheLocation: "localStorage", // This configures where your cache will be stored
                storeAuthStateInCookie: true
            }
        };
        const instance = new msal.PublicClientApplication(MSALCONFIG);
        await instance.initialize();
        return instance;
    }

    public async getAccessToken(): Promise<string | undefined> {
        // Ensure MSAL is fully initialized before attempting to get token
        await this.ensureMsalInitialized();

        return this._acquireToken();
    }

    /**
     * Internal method to acquire token without initialization check
     * Used both during initialization and after initialization is complete
     */
    private async _acquireToken(): Promise<string | undefined> {
        return SentryService.startSpan("auth.token", "Get Access Token", async (span) => {
            try {
                const apiScopes = ["api://f6f24b76-b764-45b1-9a56-fe0c18ab1f3b/user"];

                span?.setAttribute("auth.scopes", apiScopes.join(","));

                // NOTE: handleRedirectPromise is called ONCE during initialization in init()
                // Do NOT call it here
                const accounts = this.msalInstance.getAllAccounts();

                span?.setAttribute("auth.accounts_count", accounts.length);

                if (accounts.length === 0) {
                    // No accounts - this should not happen if init() flow is correct
                    // but handle gracefully by redirecting to login
                    SentryService.addBreadcrumb(
                        "No accounts found in getAccessToken, redirecting to login",
                        "auth",
                        "warn"
                    );
                    await this.msalInstance.loginRedirect({ scopes: apiScopes });
                    return undefined;
                }

                try {
                    const srequest: msal.SilentRequest = {
                        account: accounts[0],
                        scopes: apiScopes
                    };
                    const response = await this.msalInstance.acquireTokenSilent(srequest);
                    const accessToken = response.accessToken;

                    // Update user context in Sentry
                    SentryService.setUser({
                        id: accounts[0].localAccountId,
                        username: accounts[0].username,
                        name: accounts[0].name || accounts[0].username
                    });

                    SentryService.addBreadcrumb(
                        "Access token acquired successfully",
                        "auth",
                        "info",
                        { method: "silent", accountId: accounts[0].localAccountId }
                    );

                    return accessToken;
                } catch (error) {
                    if (error instanceof msal.InteractionRequiredAuthError) {
                        SentryService.addBreadcrumb(
                            "Silent token acquisition failed, redirecting to login",
                            "auth",
                            "info",
                            { error: "interaction_required" }
                        );
                        await this.msalInstance.loginRedirect({ scopes: apiScopes });
                        return undefined;
                    } else {
                        // Only capture non-interaction errors as actual errors
                        SentryService.captureError(error as Error, {
                            component: "Component",
                            operation: "acquireTokenSilent",
                            errorType: error.constructor.name
                        });
                        throw error;
                    }
                }
            } catch (error) {
                span?.setAttribute("error", true);
                throw error;
            }
        });
    }

    public async getCurrentUser() {
        // Ensure MSAL is fully initialized before attempting to get user
        await this.ensureMsalInitialized();

        return SentryService.startSpan("user.get_current", "Get Current User", async (span) => {
            try {
                const accounts = this.msalInstance.getAllAccounts();
                const oResourceBundle = await (
                    this.getModel("i18n") as ResourceModel
                )?.getResourceBundle();

                span?.setAttribute("accounts.count", accounts.length);

                if (accounts.length === 0) {
                    SentryService.addBreadcrumb("No user accounts found", "auth", "warn");
                    return { name: oResourceBundle?.getText("noUserFound") };
                }

                const activeAccount = accounts[0];
                span?.setAttribute("user.has_name", !!activeAccount.name);

                if (!activeAccount.name) {
                    SentryService.addBreadcrumb(
                        "User account found but no name available",
                        "auth",
                        "warn",
                        { accountId: activeAccount.localAccountId }
                    );
                    return { name: oResourceBundle?.getText("noNamefound") };
                }

                // Update Sentry user context
                SentryService.setUser({
                    id: activeAccount.localAccountId,
                    username: activeAccount.username,
                    name: activeAccount.name
                });

                SentryService.addBreadcrumb("Current user retrieved successfully", "user", "info", {
                    userName: activeAccount.name
                });

                return { name: activeAccount.name };
            } catch (error) {
                span?.setAttribute("error", true);
                SentryService.captureError(error as Error, {
                    component: "Component",
                    operation: "getCurrentUser"
                });
                throw error;
            }
        });
    }
    private getCurrentDateTime(): string {
        const now = new Date();
        return now.toLocaleString();
    }
}
